import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import { CodingStrategyEnum } from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useAuthenticationContext } from '../../../contexts/AuthenticationContext/hooks/useAuthenticationContext.hook';
import { environment } from '../../../environment';

import { useInitialValuesFactory as useAddressInitialValuesFactory } from '../../Address/hooks/useInitialValuesFactory.hook';

import type { FolderForm_Folder } from '../fragments/folder.fragment';

export const useInitialValuesFactory = (
  pInitialValues?: FolderForm_Folder | null,
) => {
  const { generateId } = useIdFactory();

  const referencesContext = useReferencesContext();

  const { defaultFolderWorkflow } = ensureDefined({
    defaultFolderWorkflow:
      referencesContext.folderWorkflowsIdMap[
        environment.defaultFolderWorkflowId
      ],
  });

  const { codingStrategiesCodeMap, addressTypesCodeMap } = referencesContext;

  const now = useMemo(() => new Date(), []);

  const folderAddressTypeId = addressTypesCodeMap.FolderBusinessEntity._id;
  // Stable, or the address id would be generated again at every render and the
  // untouched form would differ from its initial values.
  const addressInitialValues = useMemo(
    () => ({ typeIds: [folderAddressTypeId] }),
    [folderAddressTypeId],
  );
  const { initialValues: address } =
    useAddressInitialValuesFactory(addressInitialValues);

  const { authenticationData } = useAuthenticationContext();
  const businessCreatorId = authenticationData?.person._id ?? EMPTY_ID;

  const initialValues: FolderForm_Folder = useMemo(
    () => ({
      __typename: 'Folder' as const,
      _id: generateId(),
      code: EMPTY_STRING,
      title: EMPTY_STRING,
      description: EMPTY_STRING,
      memo: EMPTY_STRING,
      internalMemo: EMPTY_STRING,
      color: EMPTY_STRING,
      businessCreatorId,
      businessCreationDate: now.toISOString(),
      workflowId: defaultFolderWorkflow._id,
      isConfidential: false,
      internalActors: EMPTY_ARRAY,
      externalActors: EMPTY_ARRAY,
      ...omitBy(pInitialValues, isNil),
      codingConfig: {
        __typename: CodingStrategyEnum.CodingConfigManual as const,
        _id: generateId(),
        kind: CodingStrategyEnum.CodingConfigManual,
        strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
        code: EMPTY_STRING,
        ...omitBy(pInitialValues?.codingConfig, isNil),
      },
      address: {
        ...address,
        ...omitBy(pInitialValues?.address, isNil),
      },
    }),
    [
      address,
      businessCreatorId,
      codingStrategiesCodeMap.CodingConfigManual._id,
      defaultFolderWorkflow._id,
      generateId,
      now,
      pInitialValues,
    ],
  );

  return { initialValues };
};
